import React, { useState, useEffect } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { InputText, DropdownField, InputNumber } from 'components/common/input';
import BaseModal from 'components/common/modal/BaseModal';
import usePost from 'core/hooks/api/usePost';
import { colorSelect } from 'core/constant/SELECT_OPTIONS';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import { validateForm } from 'core/utils/validation';
import useFormHandlers from 'core/hooks/form/useFormHandlers';
import { useInitialFetch } from 'core/context/initialFetchContext';

const CustomizeModal = ({ isOpen, onRequestClose, product, selectedOption, selectedSize }) => {
    const [dimensions, setDimensions] = useState({ length: '', width: '', height: '' });
    const [unit, setUnit] = useState('cm');
    const [color, setColor] = useState('#ffffff');
    const [userInfo, setUserInfo] = useState({ name: '', email: '', phone: '' });
    const [designImage, setDesignImage] = useState(null);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { postData } = usePost(API_ENDPOINTS.CONTACT);
    const { handleChange } = useFormHandlers(setUserInfo);
    const { storeInformation } = useInitialFetch();

    useEffect(() => {
        setDimensions({ length: undefined, width: undefined, height: undefined });
        setUnit('cm');
        setColor('#ffffff');
        setUserInfo({ name: '', email: '', phone: '' });
        setDesignImage(null);
        setErrors({});
        setIsSubmitting(false);
    }, [product]);

    const handleUnitChange = (e) => {
        setUnit(e.value);
    };

    const handleColorChange = (e) => {
        setColor(e.value);
    };

    const handleImageChange = (e) => {
        setDesignImage(e.target.files[0]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const validationRules = [
            { field: 'name', method: 'required' },
            { field: 'email', method: 'required' },
            { field: 'phone', method: 'required' }
        ];
        const validationErrors = validateForm(userInfo, validationRules);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            setIsSubmitting(false);
            return;
        }

        const message = `
            Dimensiones: ${dimensions.length || 'N/A'} x ${dimensions.width || 'N/A'} x ${dimensions.height || 'N/A'} ${unit}
            Color: ${color}
            Nombre: ${userInfo.name}
            Correo: ${userInfo.email}
            Teléfono: ${userInfo.phone}
        `;
        const formData = {
            name: userInfo.name,
            email: userInfo.email,
            phone: userInfo.phone,
            user_type: '',
            message,
            product_id: product?.id,
            product_option_id: undefined,
        };
        await postData(formData);
        setModalIsOpen(true);
        onRequestClose();
        setIsSubmitting(false);
    };

    const handleWhatsAppClick = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const validationRules = [
            { field: 'name', method: 'required' },
            { field: 'email', method: 'required' },
            { field: 'phone', method: 'required' }
        ];
        const validationErrors = validateForm(userInfo, validationRules);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            setIsSubmitting(false);
            return;
        }

        const selectedColorLabel = colorSelect.find(option => option.value === color)?.label || color;
        const message = `
¡Hola! Me gustaría personalizar el siguiente producto:

Producto: ${product?.title}
Dimensiones: Largo ${dimensions.length} ${unit}, Ancho ${dimensions.width} ${unit}, Altura ${dimensions.height} ${unit}
Color: ${selectedColorLabel}

Mis datos de contacto son:
Nombre: ${userInfo.name}
Correo: ${userInfo.email}
Teléfono: ${userInfo.phone}
        `;
        const formData = {
            name: userInfo.name,
            email: userInfo.email,
            phone: userInfo.phone,
            user_type: '',
            message,
            product_id: product?.id,
            product_option_id: undefined,
        };
        await postData(formData);
        setIsSubmitting(false);
        setErrors({});
        const whatsappUrl = `https://wa.me/${storeInformation.whatsapp}?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setIsSubmitting(false);
    };

    const unitOptions = [
        { label: 'cm', value: 'cm' },
        { label: 'mm', value: 'mm' },
        { label: 'in', value: 'in' }
    ];

    return (
        <>
            <BaseModal
                show={isOpen}
                title="Crea con Nosotros"
                handleClose={onRequestClose}
            >
                <form onSubmit={handleSubmit}>
                    <div className="row">
                        <div className="col-md-4 col-6 form-group">
                            <InputNumber
                                label={`Largo (${unit})`}
                                value={dimensions.length || ""}
                                name="length"
                                onChange={(e) => setDimensions({ ...dimensions, length: e.value })}
                                placeholder="Ingrese el largo"
                            />
                        </div>
                        <div className="col-md-4 col-6 form-group">
                            <InputNumber
                                label={`Ancho (${unit})`}
                                value={dimensions.width || ""}
                                name="width"
                                onChange={(e) => setDimensions({ ...dimensions, width: e.value })}
                                placeholder="Ingrese el ancho"
                            />
                        </div>
                        <div className="col-md-4 col-12 form-group">
                            <InputNumber
                                label={`Altura (${unit})`}
                                value={dimensions.height || ""}
                                name="height"
                                onChange={(e) => setDimensions({ ...dimensions, height: e.value })}
                                placeholder="Ingrese la altura"
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-4 col-6 form-group">
                            <DropdownField
                                label="Unidad"
                                value={unit}
                                options={unitOptions}
                                onChange={handleUnitChange}
                                placeholder="Seleccione la unidad"
                            />
                        </div>
                        <div className="col-md-8 col-6 form-group">
                            <DropdownField
                                label="Color"
                                value={color}
                                options={colorSelect}
                                onChange={handleColorChange}
                                placeholder="Seleccione el color"
                            />
                        </div>
                    </div>
                    {color === 'fas fa-th-large' && (
                        <div className="row">
                            <div className="col-md-12    form-group">
                                <label htmlFor="designImage">Muéstranos tu diseño</label>
                                <input
                                    type="file"
                                    id="designImage"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    className="form-control"
                                />
                            </div>
                        </div>
                    )}
                    <div className="row">
                        <div className="col-md-4 col-6 form-group">
                            <InputText
                                label="Teléfono"
                                value={userInfo.phone}
                                onChange={(e) => handleChange('phone', e.target.value)}
                                name="phone"
                                placeholder="Ingrese su teléfono"
                                error={errors.phone}
                            />
                        </div>
                        <div className="col-md-4 col-6 form-group">
                            <InputText
                                label="Nombre"
                                value={userInfo.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                name="name"
                                placeholder="Ingrese su nombre"
                                error={errors.name}
                            />
                        </div>
                        <div className="col-md-4 form-group">
                            <InputText
                                label="Correo"
                                value={userInfo.email}
                                onChange={(e) => handleChange('email', e.target.value)}
                                name="email"
                                placeholder="Ingrese su correo"
                                error={errors.email}
                            />
                        </div>
                    </div>
                    <div className="d-flex justify-content-between mt-3">
                        <Button variant="primary" type="submit" disabled={isSubmitting}>
                            {isSubmitting ? (
                                <>
                                    <Spinner className="me-2" as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                    Enviando...
                                </>
                            ) : (
                                'Enviar Cotización'
                            )}
                        </Button>
                        <a href="#" onClick={handleWhatsAppClick} className="theme-btn-wsp btn" title="Contactar por WhatsApp">
                            <i className="fab fa-whatsapp" />
                            <span>WHATSAPP</span>
                        </a>
                    </div>
                </form>
            </BaseModal>
            <Modal show={modalIsOpen} onHide={closeModal}>
                <Modal.Header className="p-4 pb-0" closeButton>
                    <Modal.Title>
                        <i className="fas fa-check-circle text-success me-2"></i>
                        Formulario enviado con éxito
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <div id='product-pro-forma'>
                        <p>Gracias por contactarnos. Nos pondremos en contacto con usted pronto.</p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeModal}>
                        Cerrar
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CustomizeModal;
